<template>
  <div v-if="isVisible" class="modal-overlay" @click="closeModal">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3 class="modal-title">{{$t('billing_information')}}</h3>
        <button class="close-button" @click="closeModal">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.55323 6.37557L13.6243 13.4466C13.6456 13.468 13.6588 13.4988 13.6588 13.535C13.6588 13.5713 13.6456 13.6021 13.6243 13.6234C13.603 13.6447 13.5722 13.6579 13.5359 13.6579C13.4996 13.6579 13.4688 13.6447 13.4475 13.6234L6.37646 6.55235C6.35514 6.53103 6.34193 6.50023 6.34193 6.46396C6.34193 6.4277 6.35514 6.39689 6.37646 6.37557C6.39777 6.35426 6.42858 6.34105 6.46484 6.34105C6.50111 6.34105 6.53191 6.35426 6.55323 6.37557Z" fill="#282A36" stroke="#282A36"/>
            <path d="M13.6235 6.37557C13.6449 6.39689 13.6581 6.4277 13.6581 6.46396C13.6581 6.50023 13.6449 6.53103 13.6235 6.55235L6.55248 13.6234C6.53116 13.6447 6.50035 13.6579 6.46409 13.6579C6.42782 13.6579 6.39702 13.6447 6.3757 13.6234C6.35438 13.6021 6.34117 13.5713 6.34117 13.535C6.34117 13.4988 6.35438 13.468 6.3757 13.4466L13.4468 6.37557C13.4681 6.35426 13.4989 6.34105 13.5352 6.34105C13.5714 6.34105 13.6022 6.35426 13.6235 6.37557Z" fill="#282A36" stroke="#282A36"/>
          </svg>
        </button>
      </div>

      <div class="modal-content">
        <div v-if="isLoadingData" class="loading-container"></div>

        <form v-else @submit.prevent="saveBillingInfo">
          <div class="billing-type-section">
            <div class="radio-group">
              <label class="radio-option">
                <input
                    type="radio"
                    v-model="billingData.billingType"
                    value="individual"
                    @change="onBillingTypeChange"
                >
                <span class="radio-circle"></span>
                <span class="radio-text">{{$t('individual')}}</span>
              </label>

              <label class="radio-option">
                <input
                    type="radio"
                    v-model="billingData.billingType"
                    value="corporate"
                    @change="onBillingTypeChange"
                >
                <span class="radio-circle"></span>
                <span class="radio-text">{{$t('corporate')}}</span>
              </label>
            </div>
          </div>

          <div v-if="billingData.billingType === 'individual'" class="form-row">
            <div class="form-group">
              <label>{{$t('login_name')}}</label>
              <input
                  type="text"
                  v-model="billingData.firstName"
                  :placeholder="this.$t('login_name')"
                  class="form-input"
              >
            </div>
            <div class="form-group">
              <label>{{$t('login_surname')}}</label>
              <input
                  type="text"
                  v-model="billingData.lastName"
                  :placeholder="this.$t('login_surname')"
                  class="form-input"
              >
            </div>
          </div>

          <div v-if="billingData.billingType === 'corporate'">
            <div class="form-group">
              <label>{{$t('trade_name')}}</label>
              <input
                  type="text"
                  v-model="billingData.companyName"
                  :placeholder="this.$t('trade_name')"
                  class="form-input"
              >
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>{{$t('tax_office')}}</label>
                <input
                    type="text"
                    v-model="billingData.taxOffice"
                    :placeholder="this.$t('tax_office')"
                    class="form-input"
                >
              </div>
              <div class="form-group">
                <label>{{$t('tax_identification')}}</label>
                <input
                    type="text"
                    v-model="billingData.taxNumber"
                    :placeholder="this.$t('tax_identification')"
                    class="form-input"
                    maxlength="10"
                >
              </div>
            </div>
          </div>

          <div class="form-group">
            <label>{{$t('email')}}</label>
            <input
                type="email"
                v-model="billingData.email"
                :placeholder="$t('email')"
                class="form-input"
            >
          </div>

          <div class="form-group">
            <label>{{$t('address_person')}}</label>
            <input
                type="text"
                v-model="billingData.addressLine"
                :placeholder="this.$t('address_person')"
                class="form-input-big"
            >
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{$t('country_detail')}}</label>
              <select
                  v-model="billingData.country"
                  class="form-input"
                  @change="onCountryChange"
                  :disabled="loadingCountries"
              >
                <option value="" disabled>{{ loadingCountries ? $t('countries_loading') : $t('select_country') }}</option>
                <option
                    v-for="country in countries"
                    :key="country && country.value ? country.value : 'default'"
                    :value="country && country.value ? country.value : ''"
                >
                  {{ country && country.text ? country.text : $t('unknown_country') }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label>{{$t('city_title')}}</label>
              <select
                  v-if="billingData.country == 218"
                  v-model="billingData.city"
                  class="form-input"
                  :disabled="loadingCities"
              >
                <option value="" disabled>
                  {{ loadingCities ? $t('cities_loading') : $t('select_city') }}
                </option>
                <option
                    v-for="city in cities"
                    :key="city && city.value ? city.value : 'default'"
                    :value="city && city.value ? city.value : ''"
                >
                  {{ city && city.text ? city.text : $t('unknown_city') }}
                </option>
              </select>

              <input
                  v-else
                  type="text"
                  v-model="billingData.cityName"
                  :placeholder="this.$t('write_name_of_city')"
                  class="form-input"
                  :disabled="!billingData.country"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{$t('postal_code')}}</label>
              <input
                  type="text"
                  v-model="billingData.postalCode"
                  :placeholder="this.$t('postal_code')"
                  class="form-input"
              >
            </div>
            <div class="form-group phonebox">
              <label>{{$t('contact_no')}}</label>
              <vue-tel-input
                  v-model="billingData.phone"
                  :dropdown-options="phoneDropdownOptions"
                  :input-options="phoneInputOptions"
                  :valid-characters-only="true"
                  :auto-default-country="false"
                  :auto-format="false"
                  default-country="TR"
                  mode="national"
                  @validate="onPhoneValidate"
                  @country-changed="onCountryChanged"
                  @input="onPhoneInput"
                  ref="phoneInput"
                  class="custom-phone-input"
              />
              <div v-if="!phoneValid && billingData.phone" class="phone-error">
                {{$t('enter_valid_phone_number')}}
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="save-button" :disabled="isLoading">
              {{ isLoading ? $t('saving') : $t('save_information') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import pricingService from '@/service/pricingService';
import commonService from '@/service/commonService';

export default {
  name: 'PricingInformation',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    billingInfo: {
      type: Object,
      default: () => ({
        billingId: null,
        billingType: 'individual',
        taxNumber: '',
        taxOffice: '',
        companyName: '',
        firstName: '',
        lastName: '',
        country: '',
        city: '',
        district: '',
        addressLine: '',
        postalCode: '',
        phone: '',
        email: '',
        isDefault: true,
        isActive: true
      })
    },
    mode: {
      type: String,
      default: 'update',
      validator: value => ['add', 'update'].includes(value)
    }
  },

  data() {
    return {
      billingData: {
        billingType: 'individual',
        firstName: '',
        lastName: '',
        companyName: '',
        taxNumber: '',
        taxOffice: '',
        addressLine: '',
        country: '',
        city: '',
        cityName: '',
        postalCode: '',
        phone: '',
        email: ''
      },
      countries: [],
      cities: [],
      loadingCountries: false,
      loadingCities: false,
      isLoadingData: false,
      phoneValid: true,
      isLoading: false,
      currentCountry: null,
      maxPhoneLength: 15,
      phoneDropdownOptions: {
        showDialCodeInSelection: true,
        showFlags: true,
        showSearchBox: true,
        showDialCodeInList: true,
        tabindex: 0
      },
      phoneInputOptions: {
        placeholder: this.$t('phone_number_person'),
        maxlength: 15,
        autocomplete: 'tel'
      }
    }
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.loadInitialData();
      }
    },

    billingInfo: {
      handler() {
        if (this.isVisible && this.billingInfo) {
          this.populateForm();
        }
      },
      deep: true,
      immediate: true
    },

    'billingData.phone'(newPhone) {
      if (newPhone && this.maxPhoneLength) {
        const cleanPhone = newPhone.replace(/[\s\-\(\)]/g, '')
        if (cleanPhone.length > this.maxPhoneLength) {
          this.$nextTick(() => {
            this.billingData.phone = newPhone.slice(0, -1)
          })
        }
      }
    }
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$refs.phoneInput && this.$refs.phoneInput.activeCountry) {
        this.calculateMaxLength(this.$refs.phoneInput.activeCountry)
      }
    })
  },

  methods: {
    async loadInitialData() {
      this.isLoadingData = true;

      try {
        await Promise.all([
          this.loadCountries(),
          this.loadCities()
        ]);

        await this.populateForm();

      } catch (error) {
      } finally {
        this.isLoadingData = false;
      }
    },

    onBillingTypeChange() {
      if (this.billingData.billingType === 'individual') {
        this.billingData.companyName = '';
        this.billingData.taxNumber = '';
        this.billingData.taxOffice = '';
      } else {
        this.billingData.firstName = '';
        this.billingData.lastName = '';
      }
    },

    async loadCountries() {
      if (this.countries.length > 0) return;

      try {
        this.loadingCountries = true;

        const response = await commonService.getCountries();

        if (response && response.data && response.data.responseType === 'success') {
          const countryTemp = response.data.data && response.data.data.country ? response.data.data.country : [];
          this.countries = countryTemp.map((item) => ({
            value: item && item.id ? item.id : null,
            text: item && item.name ? item.name : this.$t('unknown_country'),
          })).filter(country => country.value !== null);

        } else {
          throw new Error(this.$t('countries_load_failed'));
        }

      } catch (error) {
        this.countries = [];
      } finally {
        this.loadingCountries = false;
      }
    },

    async loadCities(countryId) {
      if (this.cities.length > 0) return;

      try {
        this.loadingCities = true;

        const response = await commonService.getCities();

        if (response && response.data && response.data.responseType === 'success') {
          const cityTemp = response.data.data && response.data.data.cities ? response.data.data.cities : [];
          this.cities = cityTemp.map((item) => ({
            value: item && item.id ? item.id : null,
            text: item && item.name ? item.name : this.$t('unknown_city'),
          })).filter(city => city.value !== null);
        } else {
          throw new Error(this.$t('cities_load_failed'));
        }

      } catch (error) {
        this.cities = [];
      } finally {
        this.loadingCities = false;
      }
    },

    onCountryChange() {
      this.billingData.city = '';
      this.billingData.cityName = '';

      if (parseInt(this.billingData.country) !== 218) {
        this.billingData.city = null;
        this.billingData.cityName = '';
      }
    },

    async populateForm() {
      if (!this.billingInfo) {
        return;
      }

      if (this.countries.length === 0) {
        await this.loadCountries();
      }
      if (this.cities.length === 0) {
        await this.loadCities();
      }

      this.billingData = {
        billingType: this.billingInfo.billingType || 'individual',
        firstName: this.billingInfo.firstName || '',
        lastName: this.billingInfo.lastName || '',
        companyName: this.billingInfo.companyName || '',
        taxNumber: this.billingInfo.taxNumber || '',
        taxOffice: this.billingInfo.taxOffice || '',
        addressLine: this.billingInfo.addressLine || '',
        country: this.billingInfo.country ? this.billingInfo.country.toString() : '',
        city: this.billingInfo.city ? this.billingInfo.city.toString() : '',
        cityName: this.billingInfo.cityName || '',
        postalCode: this.billingInfo.postalCode || '',
        phone: this.billingInfo.phone || '',
        email: this.billingInfo.email || ''
      };
    },

    closeModal() {
      this.$emit('close');
    },

    async saveBillingInfo() {
      if (!this.validateForm()) {
        return;
      }

      this.isLoading = true;

      try {
        let response;
        let billingId = null;

        if (this.billingInfo && this.billingInfo.billingId) {
          billingId = this.billingInfo.billingId;
        }

        const validEmail = this.isValidEmail(this.billingData.email || this.billingInfo?.email)
            ? (this.billingData.email || this.billingInfo?.email)
            : null;

        let formattedPhone = '';

        if (this.$refs.phoneInput) {
          const phoneInstance = this.$refs.phoneInput;

          if (phoneInstance.phoneObject && phoneInstance.phoneObject.international) {
            formattedPhone = phoneInstance.phoneObject.international.replace(/[\s\-\(\)]/g, '');
          } else if (phoneInstance.activeCountry && phoneInstance.activeCountry.dialCode) {
            const cleanNumber = this.billingData.phone.replace(/[^\d]/g, '');
            const dialCode = phoneInstance.activeCountry.dialCode;

            let localNumber = cleanNumber;
            if (cleanNumber.startsWith(dialCode)) {
              localNumber = cleanNumber.substring(dialCode.length);
            }

            if (localNumber.startsWith('0')) {
              localNumber = localNumber.substring(1);
            }

            formattedPhone = `+${dialCode}${localNumber}`;
          } else if (this.currentCountry && this.currentCountry.dialCode) {
            const cleanNumber = this.billingData.phone.replace(/[^\d]/g, '');
            const dialCode = this.currentCountry.dialCode;

            let localNumber = cleanNumber;
            if (cleanNumber.startsWith(dialCode)) {
              localNumber = cleanNumber.substring(dialCode.length);
            }

            if (localNumber.startsWith('0')) {
              localNumber = localNumber.substring(1);
            }

            formattedPhone = `+${dialCode}${localNumber}`;
          } else {
            const cleanNumber = this.billingData.phone.replace(/[^\d]/g, '');
            if (cleanNumber.startsWith('0')) {
              formattedPhone = `+90${cleanNumber.substring(1)}`;
            } else {
              formattedPhone = `+90${cleanNumber}`;
            }
          }
        }

        const commonData = {
          billingType: this.billingData.billingType,
          taxNumber: this.billingData.taxNumber || '',
          taxOffice: this.billingData.taxOffice || '',
          companyName: this.billingData.companyName || '',
          firstName: this.billingData.firstName || '',
          lastName: this.billingData.lastName || '',
          countryId: parseInt(this.billingData.country),
          city: parseInt(this.billingData.country) === 218 ? this.billingData.city : this.billingData.cityName,
          district: (this.billingInfo && this.billingInfo.district) ? this.billingInfo.district : '',
          addressLine: this.billingData.addressLine,
          postalCode: this.billingData.postalCode,
          phone: formattedPhone,
          email: validEmail,
          isDefault: true,
          isActive: true
        };

        if (!billingId) {
          response = await pricingService.addBillingInfo(commonData);
        } else {
          response = await pricingService.updateBillingInfo({
            ...commonData,
            billingId,
            isDefault: (this.billingInfo && this.billingInfo.isDefault !== undefined) ? this.billingInfo.isDefault : true,
            isActive: (this.billingInfo && this.billingInfo.isActive !== undefined) ? this.billingInfo.isActive : true
          });
        }

        if (response && response.data && response.data.responseType === 'success') {
          const savedData = {
            ...response.data.data,
            billingId: response.data.data?.billingId || billingId
          };

          this.$emit('save', savedData);
          this.closeModal();

          const successMessage = !billingId
              ? this.$t('billing_info_added_success')
              : this.$t('billing_info_updated_success');

          this.$snackbar.add({
            text: response.data.message || successMessage,
            type: 'success'
          });
        } else {
          throw new Error((response && response.data && response.data.message) ? response.data.message : this.$t('operation_failed'));
        }

      } catch (error) {
        const errorMessage = (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          this.$t('billing_info_processing_error');

        this.$snackbar.add({
          text: errorMessage,
          type: 'error'
        });

      } finally {
        this.isLoading = false;
      }
    },
    validateForm() {
      if (this.billingData.billingType === 'individual') {
        if (!this.billingData.firstName || !this.billingData.lastName) {
          this.$snackbar.add({
            text: this.$t('first_last_name_required'),
            type: 'error'
          });
          return false;
        }
      }

      if (this.billingData.billingType === 'corporate') {
        if (!this.billingData.companyName) {
          this.$snackbar.add({
            text: this.$t('company_name_required'),
            type: 'error'
          });
          return false;
        }
        if (!this.billingData.taxOffice) {
          this.$snackbar.add({
            text: this.$t('tax_office_required'),
            type: 'error'
          });
          return false;
        }
        if (!this.billingData.taxNumber) {
          this.$snackbar.add({
            text: this.$t('tax_number_required'),
            type: 'error'
          });
          return false;
        }
        if (this.billingData.taxNumber.length !== 10) {
          this.$snackbar.add({
            text: this.$t('tax_number_must_be_10_digits'),
            type: 'error'
          });
          return false;
        }
      }

      if (!this.billingData.addressLine) {
        this.$snackbar.add({
          text: this.$t('address_required'),
          type: 'error'
        });
        return false;
      }
      if (!this.billingData.country) {
        this.$snackbar.add({
          text: this.$t('country_selection_required'),
          type: 'error'
        });
        return false;
      }
      if (parseInt(this.billingData.country) === 218) {
        if (!this.billingData.city) {
          this.$snackbar.add({
            text: this.$t('city_selection_required'),
            type: 'error'
          });
          return false;
        }
      } else {
        if (!this.billingData.cityName || !this.billingData.cityName.trim()) {
          this.$snackbar.add({
            text: this.$t('city_name_required'),
            type: 'error'
          });
          return false;
        }
      }
      if (!this.billingData.phone || !this.phoneValid) {
        this.$snackbar.add({
          text: this.$t('valid_phone_required'),
          type: 'error'
        });
        return false;
      }
      return true;
    },

    onPhoneValidate(validation) {
      if (!validation) return;

      this.phoneValid = validation.valid;

      if (validation.country && this.billingData.phone) {
        this.calculateMaxLength(validation.country)
      }
    },

    onCountryChanged(country) {
      if (!country) return;

      this.currentCountry = country;
      this.calculateMaxLength(country);
    },

    calculateMaxLength(country) {
      try {
        if (country && country.dialCode) {
          const dialCodeLength = country.dialCode.length + 1
          const knownLengths = {
            'TR': 14, 'US': 14, 'CA': 14, 'GB': 13, 'DE': 15,
            'FR': 13, 'IT': 14, 'ES': 12, 'RU': 13, 'BR': 14,
            'IN': 13, 'CN': 14, 'JP': 13
          }

          if (country.iso2 && knownLengths[country.iso2]) {
            this.maxPhoneLength = knownLengths[country.iso2]
          } else {
            const dialCodeNum = parseInt(country.dialCode)
            if (dialCodeNum < 10) this.maxPhoneLength = dialCodeLength + 10
            else if (dialCodeNum < 100) this.maxPhoneLength = dialCodeLength + 9
            else this.maxPhoneLength = dialCodeLength + 8
          }

          if (this.phoneInputOptions) {
            this.phoneInputOptions.maxlength = this.maxPhoneLength
          }
        }
      } catch (error) {
        this.maxPhoneLength = 15
      }
    },

    onPhoneInput() {
      if (this.billingData.phone) {
        let cleanPhone = this.billingData.phone.replace(/[^\d]/g, '');

        if (cleanPhone.startsWith('0')) {
          cleanPhone = cleanPhone.substring(1);
        }

        this.billingData.phone = cleanPhone;

        if (this.maxPhoneLength && cleanPhone.length > this.maxPhoneLength) {
          this.billingData.phone = cleanPhone.slice(0, this.maxPhoneLength);
        }
      }
    },

    isValidEmail(email) {
      if (!email || email === 'Bilgi bulunamadı') return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-header {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #E5E7EB;
  padding: 30px 24px;
  border-bottom: 1px solid #E5E7EB;
  justify-content: flex-start;
}

.modal-title {
  font-size: 18px;
  color: #1F2937;
  margin: 0;
  font-family: 'euclid_semi_bold' , sans-serif;
}

.close-button {
  position: absolute;
  top: 24px;
  right: 24px;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  color: #666666;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background-color: #F3F4F6;
}

.modal-content {
  padding: 24px;
}

/* Radio Button Styles */
.billing-type-section {
  margin-bottom: 24px;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: 'euclid_medium', sans-serif;
  font-size: 14px;
  color: #374151;
}

.radio-option input[type="radio"] {
  display: none;
}

.radio-circle {
  width: 18px;
  height: 18px;
  border: 2px solid #D1D5DB;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s ease;
}

.radio-option input[type="radio"]:checked + .radio-circle {
  border-color: #1F2937;
  background-color: #1F2937;
}

.radio-option input[type="radio"]:checked + .radio-circle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background-color: white;
  border-radius: 50%;
}

.radio-text {
  user-select: none;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.form-group label {
  font-size: 14px;
  color: #374151;
  margin-bottom: 6px;
  text-align: left;
}

.form-input-big{
  display: flex;
  align-self: start;
  border: none;
  outline: none;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  background-color: #f5f5f5;
  color: #282a36;
  padding: 15px;
  border-radius: 8px;
  width: 100%;
  height: 84px;
}

.form-input {
  border: none;
  outline: none;
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  background-color: #f5f5f5;
  color: #282a36;
  padding: 10px;
  border-radius: 8px;
  height: 42px;
}

.form-actions {
  margin-top: 24px;
  margin-bottom: 20px;
}

.save-button {
  display: flex;
  background: #1F2937;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 16px;
  font-size: 16px;
  cursor: pointer;
  width: 100%;
  height: 40px;
  justify-content: center;
  align-items: center;
  font-family: 'euclid_medium', sans-serif;
}

.save-button:hover {
  background: #111827;
}

.save-button:disabled {
  background: #9CA3AF;
  cursor: not-allowed;
}

.info-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
}

.info-message span {
  font-size: 13px;
  color: #6B7280;
  line-height: 1.4;
}

.phone-error {
  color: #EF4444;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'euclid_regular', sans-serif;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container span {
  font-size: 14px;
  color: #6b7280;
  font-family: 'euclid_regular', sans-serif;
}

:deep(.custom-phone-input) {
  width: 100%;
  border: none;
  box-shadow: none;
}

:deep(.custom-phone-input:hover) {
  width: 100%;
  border: none;
  box-shadow: none;
}

:deep(.custom-phone-input .vue-tel-input) {
  border-radius: 8px;
  background-color: #F5F5F5;
  border: none;
  overflow: hidden;
  height: 42px;
}

:deep(.custom-phone-input .vti__dropdown) {
  background-color: #F5F5F5;
  border: none;
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  padding: 0 12px;
  min-width: 85px;
  height: 42px;
  display: flex;
  align-items: center;
}

:deep(.custom-phone-input .vti__input) {
  background-color: #F5F5F5;
  border: none;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  font-size: 14px;
  font-family: 'euclid_regular', sans-serif;
  color: #282a36;
  height: 42px;
  box-shadow: none;
  outline: none;
}

:deep(.custom-phone-input .vti__input:focus) {
  box-shadow: none;
  outline: none;
  border: none;
}

:deep(.custom-phone-input .vti__input::placeholder) {
  color: #9CA3AF;
}

:deep(.custom-phone-input .vti__flag) {
  width: 16px;
  height: 12px;
  border-radius: 2px;
}

:deep(.vti__dropdown-list) {
  background: white;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
  width: 100% !important;
  max-width: 300px !important;
  min-width: 200px !important;
  left: 0 !important;
  right: auto !important;
}

:deep(.vti__dropdown-item) {
  padding: 8px 12px;
  font-size: 14px;
  border: none;
}

:deep(.vti__dropdown-item:hover) {
  background-color: #F3F4F6;
}

:deep(.vti__dropdown-item.highlighted) {
  background-color: #F3F4F6;
}

:deep(.vti__dropdown-arrow) {
  color: #6B7280;
}

:deep(.vti__search_box) {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  margin: 8px;
  font-size: 14px;
  width: calc(100% - 16px) !important;
  background-color: #F5F5F5;
}

:deep(.custom-phone-input .vti__dropdown) {
  position: relative;
}

:deep(.custom-phone-input .vti__dropdown.open .vti__dropdown-list) {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100% !important;
  max-width: 280px !important;
  transform: none !important;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 10px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .radio-group {
    flex-direction: column;
    gap: 16px;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-content {
    padding: 20px;
  }

  :deep(.custom-phone-input .vti__dropdown) {
    min-width: 70px;
    max-width: 80px;
  }

  :deep(.custom-phone-input .vti__dropdown.open .vti__dropdown-list) {
    max-width: 250px !important;
    width: 250px !important;
  }
}
</style>
